import { Button } from '@heidi/design-system/button';
import { TypographyH3, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import { Download, Mail, X } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { DocumentPreviewMetadata } from './ChatDocumentPreview';
import { DocumentPreviewBody } from './DocumentPreviewBody';
import { useObjectUrl } from './useObjectUrl';

/** Variant controls minor style differences between canvas and modal usage */
export type DocumentPreviewVariant = 'canvas' | 'modal';

export interface DocumentPreviewContentProps {
  /** Display title */
  title: string;
  /** Pre-signed URL to the document */
  url: string | null;
  /** Filename (used to determine preview type) */
  filename: string | null;
  /**
   * In-memory bytes. The preview creates the object URL from these and
   * revokes it on close. `url` is the remote address when the bytes are
   * not already loaded.
   */
  fileBlob?: Blob | null;
  /**
   * Optional password for encrypted PDFs. When provided, the in-app PDF
   * viewer auto-unlocks the document instead of prompting. Currently only
   * the patient-summary delivery flow supplies this.
   */
  password?: string | null;
  /**
   * PHI-safety hook: fired the first time PDF.js successfully ingests the
   * supplied `password`. Owners use this to wipe the DOB-derived secret
   * from globally-readable Jotai state immediately so it does not outlive
   * the actual document load. Ignored for non-PDF previews.
   */
  onPasswordConsumed?: () => void;
  /** Whether the document URL is being fetched */
  isLoading?: boolean;
  /** Error from fetching the document URL */
  error?: Error | null;
  /** Called when the close button is clicked */
  onClose: () => void;
  /** Called when the download button is clicked */
  onDownload: () => void;
  /** Called when the email button is clicked (omit to hide the button) */
  onEmail?: () => void;
  /** Called when the print button is clicked (omit to hide the button) */
  onPrint?: () => void;
  /** Visual variant - 'canvas' for the inline side panel, 'modal' for the dialog */
  variant?: DocumentPreviewVariant;
  /** Optional actions rendered in the header (e.g. Save as artefact). */
  headerActions?: ReactNode;
}

/**
 * DocumentPreviewContent - Shared presentational component for document previews.
 *
 * Renders the header (title, page count subtitle, close button),
 * content area (loading/error/ChatDocumentPreview), and footer (download button).
 *
 * Used by both CanvasPanel (variant='canvas') and the preview modals (variant='modal').
 * The parent provides the outer shell (side panel div or Dialog) and data resolution.
 */
export function DocumentPreviewContent({
  title,
  url,
  filename,
  fileBlob = null,
  password = null,
  isLoading = false,
  error = null,
  onClose,
  onDownload,
  onEmail,
  onPasswordConsumed,
  variant = 'canvas',
  headerActions,
}: DocumentPreviewContentProps) {
  const { t } = useTranslations();
  const [metadata, setMetadata] = useState<DocumentPreviewMetadata>({});
  const objectUrl = useObjectUrl(fileBlob);
  const previewUrl = objectUrl ?? url;
  const waitingForObjectUrl = fileBlob != null && objectUrl == null && error == null;

  const handleDownloadClick = useCallback(() => {
    if (!objectUrl) {
      onDownload();
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename || 'download';
    anchor.rel = 'noopener';
    anchor.click();
  }, [filename, objectUrl, onDownload]);

  // Reset metadata when the document changes
  useEffect(() => {
    setMetadata({});
  }, [previewUrl, filename]);

  const handleMetadata = useCallback((meta: DocumentPreviewMetadata) => {
    setMetadata(meta);
  }, []);

  const pageCountKey = metadata.pageCount === 1 ? 'chat.page_one' : 'chat.page_other';
  const subtitle = metadata.pageCount
    ? t(pageCountKey, { count: metadata.pageCount })
    : null;

  const isModal = variant === 'modal';

  return (
    <>
      {/* Header */}
      <div
        className={cn(
          'flex shrink-0 items-start justify-between',
          isModal ? 'px-6 pb-4 pt-6' : 'px-8 pb-6 pt-8'
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyH3 className="truncate font-exposure text-text-primary">
            {title}
          </TypographyH3>
          {subtitle && (
            <TypographyP2 className="text-text-secondary">{subtitle}</TypographyP2>
          )}
        </div>
        <div className="ml-4 flex shrink-0 items-center gap-2">
          {headerActions}
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={handleDownloadClick}
            disabled={!previewUrl}
            aria-label={t('chat.download')}
          >
            <Download />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label={t('chat.closePreview')}
          >
            <X />
          </Button>
        </div>
      </div>

      <DocumentPreviewBody
        labels={{
          loading: t('chat.loadingDocument'),
          failed: t('chat.failedToLoadPreview'),
          unknownError: t('chat.unknownError'),
          close: t('chat.close'),
        }}
        url={previewUrl}
        fileBlob={fileBlob ?? undefined}
        filename={filename}
        password={password}
        isLoading={isLoading || waitingForObjectUrl}
        error={error}
        onClose={onClose}
        onPasswordConsumed={onPasswordConsumed}
        onMetadata={handleMetadata}
        className={isModal ? 'px-6' : 'px-8'}
      />

      {/* Footer — only rendered when there's an action other than download
          (download lives in the header as an icon button). */}
      {onEmail && (
        <div
          className={cn(
            'flex shrink-0 items-center justify-center gap-3',
            isModal ? 'px-6 py-4' : 'px-8 py-6'
          )}
        >
          <Button variant="secondary" size="md" disabled={!previewUrl} onClick={onEmail}>
            <Mail />
            {t('chat.email')}
          </Button>
        </div>
      )}
    </>
  );
}
